import React, { useState, useEffect } from 'react';
import { Checkbox, TableCell, TableHead, TableRow, TableSortLabel } from '@mui/material';
import { styled } from '@mui/styles';
import theme from '../../../core/theme';

export interface SortableTableHeadCell<T> {
  id: keyof T;
  label: string;
  numeric: boolean;
  sortable: boolean;
}

export interface SortableTableHeadProps<T> {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof T) => void;
  order: 'asc' | 'desc';
  orderBy: string;
  headCells: SortableTableHeadCell<T>[];
  headerVariant: 'subtitle2' | 'captionBold';
  checkboxSelection?: boolean;
  setCheckboxSelectAll?: (selection: boolean) => void;
  checkboxAction?: boolean;
  reorder?: boolean;
  tableAssetType?: string;
}

function SortableTableHead<T>(props: SortableTableHeadProps<T>) {
  const {
    order,
    orderBy,
    onRequestSort,
    headCells,
    checkboxSelection,
    setCheckboxSelectAll,
    checkboxAction,
    reorder,
    tableAssetType,
  } = props;
  const createSortHandler = (property: keyof T) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  const rawAssetColumnWidths = [
    '3%', // Checkbox
    '20%', // Raw File
    '10%', // Date
    '10%', // Theme
    '10%', // Category
    '10%', // Sub Category
    '15%', // Content Folder
    '15%', // Title
    '7%', // Used
  ];

  const preFinalAndFinalAssetColumnWidths = [
    '3%', // Checkbox
    '20%', // File name
    '10%', // Theme
    '10%', // Category
    '10%', // Sub Category
    '15%', // Content Folder
    '10%', // Topic
    '15%', // Title
    '7%', // Used
  ];

  const marketingColumnWidths = [
    '180px', // File name
    '120px', // Platform
    '150px', // Title
    '80px', // Theme
    '80px', // Category
    '120px', // Sub Category
    '120px', // Content Folder
    '100px', // Published Date
    '100px', // Published Status
  ];

  const tagsColumnWidths = [
    '6%', // Id
    '12%', // Tag name
    '10%', // Content Type
    '20%', // Content Folder
    '7%', // File Type
    '20%', // File Name
    '10%', // Category
    '15%', // Sub Category
  ];

  const assetColumnWidths = [
    '5%', // Id
    '20%', // Content Folder
    '7%', // File Type
    '20%', // File Name
    '15%', // Title
    '8%', // Content Type or theme
    '10%', // Category
    '15%', // Sub Category
  ];

  const activityColumnWidths = ['65%', '10%', '10%', '15%'];

  const StyledTableCell = styled(TableCell)(() => ({
    paddingTop: '9px',
    paddingBottom: '13px',
    paddingLeft: checkboxSelection ? '0px' : '8px',
    paddingRight: checkboxSelection ? '0px' : '9px',
    borderBottom: `1px solid ${theme.palette.chipBg.main}`,
    borderTop: `1px solid ${theme.palette.chipBg.main}`,
  }));

  const [selectAll, setSelectAll] = useState(false);
  const [columnWidths, setColumnWidths] = useState(rawAssetColumnWidths);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectAll(event.target.checked);
    if (setCheckboxSelectAll) {
      setCheckboxSelectAll(event.target.checked);
    }
  };

  const getTableWidth = () => {
    switch (tableAssetType) {
      case 'raw':
        return rawAssetColumnWidths;
      case 'pre-final':
      case 'final':
        return preFinalAndFinalAssetColumnWidths;
      case 'marketing':
        return marketingColumnWidths;
      case 'tags':
        return tagsColumnWidths;
      case 'assetType':
        return assetColumnWidths;
      case 'activities':
        return activityColumnWidths;
      default:
        return rawAssetColumnWidths;
    }
  };

  useEffect(() => {
    const columnWidthSet = getTableWidth();
    setColumnWidths(columnWidthSet || rawAssetColumnWidths);
  }, []);

  useEffect(() => {
    if (checkboxAction) {
      setSelectAll(false);
    }
  }, [checkboxAction]);

  return (
    <TableHead style={{ height: tableAssetType === 'activities' ? '42px' : '' }}>
      <TableRow>
        {reorder && <StyledTableCell component="td" />}
        {checkboxSelection && (
          <StyledTableCell component="td" style={{ width: columnWidths[0] }}>
            <Checkbox
              size="small"
              sx={{
                opacity: 0.62,
                color: selectAll ? '#FFFFFF !important' : '#E1E3E8',
              }}
              checked={selectAll}
              onChange={handleSelectAll}
            />
          </StyledTableCell>
        )}
        {headCells.map((headCell, index) => (
          <StyledTableCell
            component="th"
            style={{ width: checkboxSelection ? columnWidths[index + 1] : columnWidths[index] }}
            key={headCell.id.toString()}
            align={headCell.numeric ? 'left' : 'left'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.sortable && (
              <TableSortLabel
                active={true}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                <div style={{ fontWeight: orderBy === headCell.id ? 'bold' : 'normal' }}>{headCell.label}</div>
              </TableSortLabel>
            )}
            {!headCell.sortable && <div>{headCell.label}</div>}
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export default SortableTableHead;
